import type { DeliveryKind } from '@ui/components/features/calendar/directBooking';
import type { AvailableSlot } from '@ui/components/features/calendar/types';

import type { SlotLocationData } from './bookingSchema';
import { needsAddressForSlot } from './bookingValidators';

/**
 * Join a slot venue location's address parts into a single display line.
 * Returns `undefined` when no usable address component is present (e.g. an
 * online or at_home location, which carry no fixed street address).
 */
export function formatSlotVenueAddress(location: SlotLocationData): string | undefined {
  const parts = [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.state,
    location.zipCode,
    location.country
  ].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/**
 * Resolve the booking surfaces' shared "is this online / at-home?" decision.
 * Prefers the SELECTED slot's resolved location (per-slot kind / at-home flag)
 * and falls back to the activity-level hints (the form's `locationKind` and the
 * at-home activity flag) only when no per-slot location was captured. Both the
 * review summary and the booking-summary card key off THIS so they can't drift.
 */
export function resolveSlotLocationKind(
  slotLocation: SlotLocationData | undefined,
  fallbackLocationKind: 'venue' | 'at_home' | 'online' | undefined,
  fallbackIsAtHome: boolean
): { isOnline: boolean; isAtHome: boolean } {
  return {
    isOnline: slotLocation ? slotLocation.kind === 'online' : fallbackLocationKind === 'online',
    isAtHome: slotLocation ? slotLocation.isAtHome : fallbackIsAtHome
  };
}

/**
 * Per-occurrence variant of `needsAddressForSlot`: does booking THIS occurrence
 * (in its currently effective delivery kind) require a client address? Reuses the
 * single rule, gating on the occurrence's own resolved in-person kind
 * (`physicalKind`) and falling back to the activity-level at-home hint for legacy
 * occurrences. When the online mode is in effect, no address is ever needed.
 */
export function needsAddressForOccurrence(
  occurrence: Pick<AvailableSlot, 'physicalKind' | 'isAtHome' | 'deliveryMode'>,
  effectiveKind: DeliveryKind
): boolean {
  if (effectiveKind === 'online') return false;
  return needsAddressForSlot(
    occurrence.physicalKind,
    occurrence.isAtHome ?? false,
    occurrence.deliveryMode === 'online' ? 'online' : undefined
  );
}

/**
 * Compose a venue's display string from its optional label and formatted
 * address. When the label IS the address (a common case for a quick venue named
 * after its street), show it once instead of "addr · addr". Returns `undefined`
 * when neither is present.
 */
export function formatVenueLabelAndAddress(
  label: string | null | undefined,
  address: string | null | undefined
): string | undefined {
  const cleanLabel = label?.trim();
  const cleanAddress = address?.trim();
  if (cleanLabel && cleanAddress) {
    return cleanLabel.toLowerCase() === cleanAddress.toLowerCase() ? cleanAddress : `${cleanLabel} · ${cleanAddress}`;
  }
  return cleanLabel || cleanAddress || undefined;
}
